/**
 * Vuex Store Module: bulkEdit
 *
 * Manages state for the bulk edit functionality including:
 * - Product data loading
 * - Filter state and filtered products
 * - Selection state for bulk operations
 * - Pagination state
 *
 * This module follows the namespaced Vuex pattern consistent with
 * other modules in the application (dashboard.js, order.js, etc.)
 */

import Vue from 'vue';
import bulkEditService from '@/api/bulkEdit/bulkEditService.js';
import { buildSuggestionRequest, chunk, GPC_SUGGESTION_BLOCK_SIZE } from '@/utils/gpcSuggestions.js';

export const bulkEdit = {
  namespaced: true,

  state: () => ({
    /**
     * Loading state for async operations
     * @type {Boolean}
     */
    loading: false,

    /**
     * Saving state during batch update operations (Story 3-5)
     * @type {Boolean}
     */
    saving: false,

    /**
     * Save success flag (used for unsaved changes guard - Story 3-6)
     * @type {Boolean}
     */
    saveSuccess: false,

    /**
     * Array of all products loaded from API
     * Each product contains productInformation.basicInformation with filterable fields
     * @type {Array}
     */
    products: [],

    /**
     * Filter values for each filterable column
     * All filters use AND logic (product must match all active filters)
     * @type {Object}
     */
    filters: {
      productBrandName: '',
      tradeItemDescription: '',
      descriptionShort: '',
      functionalName: '',
      gpcCategoryCode: ''
    },

    /**
     * Current page for pagination (1-indexed)
     * Resets to 1 when filters change
     * @type {Number}
     */
    currentPage: 1,

    /**
     * Number of items to show per page
     * @type {Number}
     */
    itemsPerPage: 50,

    /**
     * Array of selected product IDs for bulk operations
     * @type {Array}
     */
    selectedProducts: [],

    /**
     * Current sort column key (e.g., 'productBrandName') or null for no sort
     * @type {String|null}
     */
    sortBy: null,

    /**
     * Current sort direction ('asc' | 'desc' | null)
     * @type {String|null}
     */
    sortDirection: null,

    /**
     * List of GPC codes loaded from API
     * Cached in state after first load
     * @type {Array}
     */
    gpcCodesList: [],

    /**
     * Loading state for GPC codes fetch
     * @type {Boolean}
     */
    gpcCodesLoading: false,

    /**
     * Array of imported product transaction IDs (from Excel import)
     * Used to filter products to show only recently imported ones
     * @type {Array<String>}
     */
    importedProductIds: [],

    /**
     * Flag to filter products to only show imported ones
     * When true, filteredProducts only returns products with IDs in importedProductIds
     * @type {Boolean}
     */
    filterImportedOnly: false,

    /**
     * AI generation progress state
     * Tracks progress of batch functional name generation after import
     * @type {Object}
     */
    aiGenerationProgress: {
      isGenerating: false,
      total: 0,
      completed: 0,
      current: null // Current product being processed
    },

    /**
     * GPC proposals per product id: { code, name, comparableProducts, gtins }.
     * Only present while the proposal is still the value in the GPC field.
     * @type {Object}
     */
    gpcSuggestions: {},

    /**
     * Product ids a proposal was already requested for in this opening
     * @type {Object}
     */
    gpcRequestedIds: {},

    /**
     * Product ids whose GPC cell currently has focus
     * @type {Object}
     */
    gpcFocusedIds: {}
  }),

  getters: {
    /**
     * GPC proposal for a product id, or null
     */
    gpcSuggestionFor: (state) => (productId) => state.gpcSuggestions[productId] || null,

    /**
     * Get filtered products based on active filters
     *
     * Applies AND logic: product must match ALL active filters
     * Uses case-insensitive substring matching for text fields
     * Uses exact match for GPC code (autocomplete selection)
     * Implements fail-fast optimization (exits on first non-match)
     * Includes error boundary for malformed data
     *
     * Performance:
     * - Time complexity: O(n*m) where n=products, m=active filters
     * - Space complexity: O(k) where k=filtered products (references only)
     * - Typical case: 100 products × 5 filters = ~500 operations, <50ms
     *
     * @param {Object} state - Vuex state
     * @returns {Array} Array of products matching all active filters
     *
     * @example
     * // With filters: { productBrandName: 'nike', gpcCategoryCode: '10005726' }
     * // Returns only products where brand contains 'nike' AND gpcCode exactly equals '10005726'
     */
    filteredProducts(state) {
      try {
        // Step 1: Start with all products or filter to imported only
        let productsToFilter = state.products;

        // Apply imported products filter if active
        if (state.filterImportedOnly && state.importedProductIds.length > 0) {
          productsToFilter = state.products.filter(product => {
            const productId = product.transactionId || product._id;
            return state.importedProductIds.includes(productId);
          });
        }

        // Step 2: Apply column filters (AND logic)
        return productsToFilter.filter(product => {
          // Defensive: Check for malformed data
          // Note: parseDashboardData flattens basicInformation into productInformation
          const pi = product?.productInformation;

          if (!pi) {
            // Log malformed product (in production, send to monitoring)
            if (process.env.NODE_ENV !== 'production') {
              console.warn('Malformed product data:', product);
            }
            return false; // Exclude malformed products
          }

          // Use flattened structure (fields are in productInformation directly)
          const bi = pi;

          // Check each filter (AND logic)
          for (const [key, filterValue] of Object.entries(state.filters)) {
            // Skip empty filters
            if (filterValue && filterValue.trim() !== '') {
              const productValue = bi[key] || '';

              let match;

              // GPC code: Exact match (autocomplete selection)
              if (key === 'gpcCategoryCode') {
                match = productValue === filterValue;
              } else {
                // Other fields: Case-insensitive substring match
                match = productValue.toLowerCase().includes(filterValue.toLowerCase());
              }

              // Fail fast - if any filter doesn't match, exclude this product
              if (!match) return false;
            }
          }

          // Passed all filters
          return true;
        });
      } catch (error) {
        // Catch unexpected errors
        console.error('Error filtering products:', error);

        // In production, send to error monitoring service
        // if (window.errorMonitoring) {
        //   window.errorMonitoring.captureException(error);
        // }

        // Return unfiltered products as fallback (fail-safe)
        return state.products;
      }
    },

    /**
     * Get sorted products based on active sort
     *
     * Applies sorting to filtered products (getter chain: filteredProducts → sortedProducts)
     * Uses locale-aware comparison (Dutch) for alphabetical fields
     * Uses numeric comparison for GPC codes
     * Implements three-state sort cycle: None → Asc → Desc → None
     *
     * Performance:
     * - Time complexity: O(n log n) where n=filtered products
     * - Space complexity: O(n) for cloned array (shallow copy)
     * - Typical case: 100 products, ~664 comparisons, <20ms
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters (filteredProducts)
     * @returns {Array} Array of sorted products (operates on filtered results)
     *
     * @example
     * // With sortBy='productBrandName', sortDirection='asc'
     * // Returns filtered products sorted alphabetically A→Z
     */
    sortedProducts(state, getters) {
      const filtered = getters.filteredProducts; // Sort operates on filtered results

      if (!state.sortBy || !state.sortDirection) {
        return filtered; // No sort applied - return default order
      }

      // Clone array to avoid mutating Vuex state
      const sorted = [...filtered];

      sorted.sort((a, b) => {
        // Use flattened structure (fields are in productInformation directly)
        const aVal = a.productInformation?.[state.sortBy] || '';
        const bVal = b.productInformation?.[state.sortBy] || '';

        // Determine comparison
        let comparison = 0;

        // Numeric comparison for GPC code
        if (state.sortBy === 'gpcCategoryCode') {
          const aNum = parseInt(aVal, 10) || 0;
          const bNum = parseInt(bVal, 10) || 0;
          comparison = aNum - bNum;
        } else {
          // Locale-aware string comparison (Dutch)
          comparison = aVal.localeCompare(bVal, 'nl', {
            sensitivity: 'base', // Ignore accents/case (Ä sorts with A)
            numeric: true // Natural sorting (handles numbers in strings)
          });
        }

        // Apply direction
        return state.sortDirection === 'asc' ? comparison : -comparison;
      });

      return sorted;
    },

    /**
     * Get current page of sorted products
     *
     * UPDATED: Now operates on sortedProducts instead of filteredProducts
     * This ensures pagination shows sorted results
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters
     * @returns {Array} Current page of products
     */
    paginatedProducts(state, getters) {
      const sorted = getters.sortedProducts; // CHANGED: was filteredProducts
      const start = (state.currentPage - 1) * state.itemsPerPage;
      const end = start + state.itemsPerPage;
      return sorted.slice(start, end);
    },

    /**
     * Get total number of pages based on sorted products
     *
     * UPDATED: Uses sortedProducts to ensure page count matches sorted results
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters
     * @returns {Number} Total number of pages
     */
    totalPages(state, getters) {
      const sorted = getters.sortedProducts; // Uses sorted products
      return Math.ceil(sorted.length / state.itemsPerPage);
    },

    /**
     * Check if there are any active filters
     *
     * @param {Object} state - Vuex state
     * @returns {Boolean} True if at least one filter has a value
     */
    hasActiveFilters(state) {
      return Object.values(state.filters).some(val => val && val.trim() !== '');
    },

    /**
     * Get loading state
     *
     * @param {Object} state - Vuex state
     * @returns {Boolean} Loading status
     */
    isLoading(state) {
      return state.loading;
    },

    /**
     * Get GPC codes list
     *
     * @param {Object} state - Vuex state
     * @returns {Array} Array of GPC codes
     */
    gpcCodes(state) {
      return state.gpcCodesList;
    },

    /**
     * Get GPC codes loading state
     *
     * @param {Object} state - Vuex state
     * @returns {Boolean} True if GPC codes are being fetched
     */
    gpcCodesLoading(state) {
      return state.gpcCodesLoading;
    },

    /**
     * Get original value for a product field (used for undo functionality)
     *
     * @param {Object} state - Vuex state
     * @returns {Function} Function that takes (productId, field) and returns original value
     */
    getOriginalValue: (state) => (productId, field) => {
      const product = state.products.find(p => p._id === productId);
      return product?.originalData?.productInformation?.[field] || null;
    },

    /**
     * Check if any VISIBLE product has validation errors (Story 3-5)
     *
     * Runs all validation rules (V1-V4) across filtered products only.
     * Used to enable/disable the save button.
     *
     * UPDATED: Now checks only filteredProducts (visible products) instead of all products.
     * This ensures validation only applies to products currently shown in the overview.
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters (filteredProducts)
     * @returns {Boolean} True if any validation errors exist in visible products
     */
    hasValidationErrors(state, getters) {
      return getters.filteredProducts.some(product => {
        // Use flattened structure - fields are in productInformation directly
        const pi = product.productInformation;

        if (!pi) return true; // Malformed data = error

        // V1: All fields required
        const requiredFields = ['productBrandName', 'tradeItemDescription', 'descriptionShort', 'functionalName', 'gpcCategoryCode'];
        const hasEmptyField = requiredFields.some(field => !pi[field] || pi[field].trim() === '');
        if (hasEmptyField) return true;

        // V2: GPC code exact 8 digits
        if (pi.gpcCategoryCode && !/^\d{8}$/.test(pi.gpcCategoryCode)) {
          return true;
        }

        // V3: Functional name min 2 characters
        if (pi.functionalName && pi.functionalName.trim().length < 2) {
          return true;
        }

        // V4: Functional name cannot contain brand words (>2 chars)
        if (pi.functionalName && pi.productBrandName) {
          const brandWords = pi.productBrandName
            .toLowerCase()
            .split(/\s+/)
            .filter(word => word.length > 2);

          const funcNameLower = pi.functionalName.toLowerCase();
          const hasConflict = brandWords.some(word => funcNameLower.includes(word));

          if (hasConflict) return true;
        }

        return false; // No errors found
      });
    },

    /**
     * Check if a product is "ready" (all required fields filled)
     * A product is ready when all 5 required fields have values:
     * - productBrandName, tradeItemDescription, descriptionShort, functionalName, gpcCategoryCode
     *
     * @returns {Function} Function that takes (product) and returns boolean
     */
    isProductReady: () => (product) => {
      const pi = product?.productInformation;
      if (!pi) return false;

      const requiredFields = ['productBrandName', 'tradeItemDescription', 'descriptionShort', 'functionalName', 'gpcCategoryCode'];

      return requiredFields.every(field => {
        const value = pi[field];
        return value && value.toString().trim() !== '';
      });
    },

    /**
     * Count how many VISIBLE products are "ready" (all required fields filled)
     * Checks only filtered products (products currently shown in the overview)
     *
     * UPDATED: Now counts only filteredProducts instead of all products.
     * This ensures the count matches what the user sees in the table.
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters (filteredProducts, isProductReady)
     * @returns {Number} Count of ready products in the current view
     */
    readyProductsCount(state, getters) {
      return getters.filteredProducts.filter(product => getters.isProductReady(product)).length;
    },

    /**
     * Check if ALL VISIBLE products are ready
     * Save button should only be enabled when this is true
     *
     * UPDATED: Now checks only filteredProducts instead of all products.
     * This ensures the save button is enabled when all visible products are complete,
     * regardless of products hidden by filters (e.g., after import).
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters (filteredProducts, readyProductsCount)
     * @returns {Boolean} True if all visible products are ready
     */
    allProductsReady(state, getters) {
      if (getters.filteredProducts.length === 0) return false;
      return getters.readyProductsCount === getters.filteredProducts.length;
    },

    /**
     * Get count of imported products that exist in the products list
     * Used to display "(X van Y)" in the import filter toggle
     *
     * @param {Object} state - Vuex state
     * @returns {Number} Count of imported products found in products list
     */
    importedProductsCount(state) {
      if (state.importedProductIds.length === 0) return 0;

      return state.products.filter(product => {
        const productId = product.transactionId || product._id;
        return state.importedProductIds.includes(productId);
      }).length;
    },

    /**
     * Check if import filter is available (has imported products)
     *
     * @param {Object} state - Vuex state
     * @param {Object} getters - Other getters
     * @returns {Boolean} True if there are imported products to filter on
     */
    hasImportedProducts(state, getters) {
      return getters.importedProductsCount > 0;
    }
  },

  mutations: {
    /**
     * Set products list
     *
     * Performance optimized: Direct assignment instead of deep copy
     * Products from API are immutable, so deep copy is unnecessary
     *
     * @param {Object} state - Vuex state
     * @param {Array} products - Array of product objects
     */
    SET_PRODUCTS(state, products) {
      state.products = products;
    },

    /**
     * Set filter values
     *
     * IMPORTANT: Resets pagination to page 1 when filters change
     * This prevents showing empty pages when filtered results are fewer than current page
     *
     * @param {Object} state - Vuex state
     * @param {Object} filters - New filter values
     */
    SET_FILTERS(state, filters) {
      state.filters = { ...filters };
      // Reset to page 1 when filters change to avoid empty pages
      state.currentPage = 1;
    },

    /**
     * Set current page
     *
     * @param {Object} state - Vuex state
     * @param {Number} page - Page number (1-indexed)
     */
    SET_CURRENT_PAGE(state, page) {
      state.currentPage = page;
    },

    /**
     * Set items per page
     *
     * @param {Object} state - Vuex state
     * @param {Number} itemsPerPage - Number of items per page
     */
    SET_ITEMS_PER_PAGE(state, itemsPerPage) {
      state.itemsPerPage = itemsPerPage;
      // Reset to page 1 when items per page changes
      state.currentPage = 1;
    },

    /**
     * Set loading state
     *
     * @param {Object} state - Vuex state
     * @param {Boolean} loading - Loading status
     */
    SET_LOADING(state, loading) {
      state.loading = loading;
    },

    /**
     * Set selected products
     *
     * @param {Object} state - Vuex state
     * @param {Array} selectedProducts - Array of selected product IDs
     */
    SET_SELECTED_PRODUCTS(state, selectedProducts) {
      state.selectedProducts = selectedProducts;
    },

    /**
     * Clear all filters
     *
     * @param {Object} state - Vuex state
     */
    CLEAR_FILTERS(state) {
      state.filters = {
        productBrandName: '',
        tradeItemDescription: '',
        descriptionShort: '',
        functionalName: '',
        gpcCategoryCode: ''
      };
      state.currentPage = 1;
    },

    /**
     * Set sort column and direction
     *
     * @param {Object} state - Vuex state
     * @param {Object} payload - { column, direction }
     * @param {String|null} payload.column - Column key or null
     * @param {String|null} payload.direction - 'asc' | 'desc' | null
     */
    SET_SORT(state, { column, direction }) {
      state.sortBy = column;
      state.sortDirection = direction;
    },

    /**
     * Update a single product field value
     *
     * Used by EditableCell for real-time updates during inline editing.
     * Tracks original values for undo functionality and marks product as modified.
     *
     * @param {Object} state - Vuex state
     * @param {Object} payload - { productId, field, value }
     * @param {String} payload.productId - Product transaction ID
     * @param {String} payload.field - Field key (e.g., 'productBrandName')
     * @param {String} payload.value - New field value
     */
    UPDATE_PRODUCT_FIELD(state, { productId, field, value }) {
      const product = state.products.find(p => p._id === productId);

      if (product) {
        // Ensure originalData exists (set when product is loaded)
        if (!product.originalData) {
          Vue.set(product, 'originalData', JSON.parse(JSON.stringify(product)));
        }

        // Update the field value
        // Note: parseDashboardData flattens basicInformation into productInformation
        if (!product.productInformation) {
          Vue.set(product, 'productInformation', {});
        }

        // Use Vue.set to ensure reactivity
        Vue.set(product.productInformation, field, value);

        // Any change of the GPC (pick, clear, bulk apply, undo) ends the "Voorstel" marker
        if (field === 'gpcCategoryCode') {
          Vue.delete(state.gpcSuggestions, productId);
        }

        // Mark product as modified
        Vue.set(product, 'isModified', true);
      }
    },

    /**
     * Set product modified flag
     *
     * Used to manually set/clear the modified flag, e.g., after undo operations
     * that restore all fields to original values.
     *
     * @param {Object} state - Vuex state
     * @param {Object} payload - { productId, isModified }
     * @param {String} payload.productId - Product transaction ID
     * @param {Boolean} payload.isModified - Whether product has unsaved changes
     */
    SET_PRODUCT_MODIFIED(state, { productId, isModified }) {
      const product = state.products.find(p => p._id === productId);

      if (product) {
        product.isModified = isModified;
      }
    },

    /**
     * Set saving state (Story 3-5)
     *
     * @param {Object} state - Vuex state
     * @param {Boolean} saving - Whether batch save is in progress
     */
    SET_SAVING(state, saving) {
      state.saving = saving;
    },

    /**
     * Set save success flag (Story 3-6)
     *
     * @param {Object} state - Vuex state
     * @param {Boolean} success - Whether save was successful
     */
    SET_SAVE_SUCCESS(state, success) {
      state.saveSuccess = success;
    },

    /**
     * Set GPC codes list
     *
     * @param {Object} state - Vuex state
     * @param {Array} codes - Array of GPC codes
     */
    SET_GPC_CODES(state, codes) {
      state.gpcCodesList = codes || [];
    },

    /**
     * Set GPC codes loading state
     *
     * @param {Object} state - Vuex state
     * @param {Boolean} loading - Loading state
     */
    SET_GPC_CODES_LOADING(state, loading) {
      state.gpcCodesLoading = loading;
    },

    /**
     * Set imported product IDs (from Excel import)
     * Used to filter Bulk Edit to show only recently imported products
     *
     * @param {Object} state - Vuex state
     * @param {Array<String>} productIds - Array of transaction IDs
     */
    SET_IMPORTED_PRODUCT_IDS(state, productIds) {
      state.importedProductIds = productIds || [];
    },

    /**
     * Set filter flag to show only imported products
     *
     * @param {Object} state - Vuex state
     * @param {Boolean} filterOnly - Whether to filter to imported products only
     */
    SET_FILTER_IMPORTED_ONLY(state, filterOnly) {
      state.filterImportedOnly = filterOnly;
      // Reset to page 1 when filter changes
      state.currentPage = 1;
    },

    /**
     * Clear imported products filter state
     * Resets both the IDs array and the filter flag
     *
     * @param {Object} state - Vuex state
     */
    CLEAR_IMPORTED_FILTER(state) {
      state.importedProductIds = [];
      state.filterImportedOnly = false;
    },

    /**
     * Set AI generation progress state
     *
     * @param {Object} state - Vuex state
     * @param {Object} progress - Progress object
     * @param {Boolean} progress.isGenerating - Whether generation is in progress
     * @param {Number} progress.total - Total products to process
     * @param {Number} progress.completed - Number of completed products
     * @param {String|null} progress.current - Current product name being processed
     */
    SET_AI_GENERATION_PROGRESS(state, progress) {
      state.aiGenerationProgress = { ...state.aiGenerationProgress, ...progress };
    },

    /**
     * Reset AI generation progress to initial state
     *
     * @param {Object} state - Vuex state
     */
    RESET_AI_GENERATION_PROGRESS(state) {
      state.aiGenerationProgress = {
        isGenerating: false,
        total: 0,
        completed: 0,
        current: null
      };
    },

    /**
     * Forget all GPC proposal state (called on each mount of the batch screen)
     */
    RESET_GPC_SUGGESTIONS(state) {
      state.gpcSuggestions = {};
      state.gpcRequestedIds = {};
      state.gpcFocusedIds = {};
    },

    /**
     * Record whether the GPC cell of a product has focus
     */
    SET_GPC_CELL_FOCUS(state, { productId, focused }) {
      if (focused) {
        Vue.set(state.gpcFocusedIds, productId, true);
      } else {
        Vue.delete(state.gpcFocusedIds, productId);
      }
    },

    /**
     * Remember that a proposal was requested for these product ids
     */
    MARK_GPC_REQUESTED(state, ids) {
      ids.forEach(id => Vue.set(state.gpcRequestedIds, id, true));
    },

    /**
     * Store the proposal behind an applied GPC (commit after UPDATE_PRODUCT_FIELD,
     * which clears the marker)
     */
    SET_GPC_SUGGESTION(state, { productId, suggestion }) {
      Vue.set(state.gpcSuggestions, productId, suggestion);
    }
  },

  actions: {
    /**
     * Load products from API
     *
     * @param {Object} context - Vuex action context
     * @param {String} informationProvider - GLN of information provider (reserved for future use)
     */
    // eslint-disable-next-line no-unused-vars
    async loadProducts({ commit }, informationProvider) {
      commit('SET_LOADING', true);
      try {
        const ProductService = require('@/api/products/productsService.js').default;
        const products = await ProductService.getIncompleteRegistration();

        // Transform products to include required fields for bulk edit
        const transformedProducts = (products || []).map(product => ({
          ...product,
          _id: product.transactionId || product._id, // Ensure _id exists for templates
          isModified: false, // Track if product has been edited
          originalData: JSON.parse(JSON.stringify(product)) // Store original for undo
        }));

        console.log('Loaded products for bulk edit:', transformedProducts.length);
        commit('SET_PRODUCTS', transformedProducts);
      } catch (error) {
        console.error('Error loading products:', error);
        commit('SET_PRODUCTS', []); // Set empty array on error
      } finally {
        commit('SET_LOADING', false);
      }
    },

    /**
     * Update filter values
     *
     * @param {Object} context - Vuex action context
     * @param {Object} filters - New filter values
     */
    setFilters({ commit }, filters) {
      commit('SET_FILTERS', filters);
    },

    /**
     * Clear all filters
     *
     * @param {Object} context - Vuex action context
     */
    clearFilters({ commit }) {
      commit('CLEAR_FILTERS');
    },

    /**
     * Set current page
     *
     * @param {Object} context - Vuex action context
     * @param {Number} page - Page number
     */
    setCurrentPage({ commit }, page) {
      commit('SET_CURRENT_PAGE', page);
    },

    /**
     * Set items per page
     *
     * @param {Object} context - Vuex action context
     * @param {Number} itemsPerPage - Items per page
     */
    setItemsPerPage({ commit }, itemsPerPage) {
      commit('SET_ITEMS_PER_PAGE', itemsPerPage);
    },

    /**
     * Update selected products
     *
     * @param {Object} context - Vuex action context
     * @param {Array} selectedProducts - Array of selected product IDs
     */
    setSelectedProducts({ commit }, selectedProducts) {
      commit('SET_SELECTED_PRODUCTS', selectedProducts);
    },

    /**
     * Update sort column and direction
     *
     * @param {Object} context - Vuex action context
     * @param {Object} payload - { column, direction }
     * @param {String|null} payload.column - Column key or null
     * @param {String|null} payload.direction - 'asc' | 'desc' | null
     */
    setSort({ commit }, { column, direction }) {
      commit('SET_SORT', { column, direction });
    },

    /**
     * Batch update products (Story 3-5) - OPTIMIZED
     *
     * Uses the new batch update endpoint to process all products in a SINGLE API call.
     * This is much faster than the previous chunked approach because:
     * - Only 1 HTTP request instead of N requests
     * - Backend loads validation lookup tables once (not per product)
     * - Reduced network overhead and connection management
     *
     * Performance:
     * - 50 products in ~3-5 seconds (was 30-60 seconds with individual calls)
     * - Single HTTP request regardless of product count
     *
     * @param {Object} context - Vuex action context
     * @param {Array} modifiedProducts - Array of products with isModified flag
     * @returns {Promise<Object>} Result with { success, succeeded, failed }
     * @throws {Error} If API request fails entirely
     */
    async batchUpdateProducts({ commit }, modifiedProducts) {
      commit('SET_SAVING', true);

      try {
        // Import service
        const bulkEditService = require('@/api/bulkEdit/bulkEditService.js').default;

        // Get user context from localStorage
        const client = JSON.parse(localStorage.getItem('client'));
        const GLN = client.informationProvider;
        const userId = JSON.parse(localStorage.getItem('userId')) || 100234;

        // Build products array for batch update
        const products = modifiedProducts.map(product => {
          // Use flattened structure directly - basicInformation is deleted during parsing
          const pi = product.productInformation;

          // Format date from DD-MM-YYYY to YYYY-MM-DD if needed
          let formattedStartDate = pi.productStartDate;
          if (formattedStartDate && formattedStartDate.includes('-')) {
            const parts = formattedStartDate.split('-');
            // Check if it's DD-MM-YYYY format (day is first, year is last and 4 digits)
            if (parts.length === 3 && parts[2].length === 4) {
              formattedStartDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
          }

          return {
            documentId: product.transactionId,
            productInformation: {
              basicInformation: {
                // Include all required fields from original data
                productArticleNumber: pi.productArticleNumber,
                productBrandName: pi.productBrandName,
                productName: pi.productName,
                productStartDate: formattedStartDate,
                productTargetMarkets: Array.isArray(pi.productTargetMarkets)
                  ? pi.productTargetMarkets.map(m => m.code || m)
                  : pi.productTargetMarkets,
                descriptionShort: pi.descriptionShort,
                tradeItemDescription: pi.tradeItemDescription
              },
              extendedInformation: {
                ...pi.extendedInformation,
                functionalName: pi.functionalName,
                gpcCategoryCode: pi.gpcCategoryCode
              }
            },
            productPackagingHierarchy: product.productPackagingHierarchy || []
          };
        });

        // Single batch update call - processes ALL products in one request!
        const result = await bulkEditService.batchUpdate({
          metaData: {
            userId,
            informationProvider: GLN
          },
          products
        });

        // Check result
        if (result.success) {
          commit('SET_SAVING', false);
          commit('SET_SAVE_SUCCESS', true);

          // Log any partial failures for debugging
          if (result.summary.failed > 0) {
            console.warn(`Batch update: ${result.summary.succeeded}/${result.summary.total} succeeded`);
            console.warn('Failed products:', result.results.failed);
          }

          // Return detailed result for component to handle
          return {
            success: true,
            total: result.summary.total,
            succeeded: result.summary.succeeded,
            failed: result.summary.failed,
            failedProducts: result.results.failed
          };
        } else {
          throw new Error(result.error || 'Batch update failed');
        }

      } catch (error) {
        commit('SET_SAVING', false);
        console.error('BATCH UPDATE ERROR:', error);
        throw error; // Re-throw for component to handle
      }
    },

    /**
     * Fetch GPC codes from API (Story 3-9)
     *
     * Loads GPC codes from backend endpoint and stores in state.
     * Uses loading flag to prevent duplicate API calls.
     * Shows user-friendly error notification on failure.
     *
     * @param {Object} context - Vuex action context
     * @returns {Promise<void>}
     */
    async fetchGpcCodes({ commit }) {
      commit('SET_GPC_CODES_LOADING', true);
      try {
        const bulkEditService = require('@/api/bulkEdit/bulkEditService.js').default;
        const response = await bulkEditService.getGpcCodes();
        commit('SET_GPC_CODES', response.data);
      } catch (error) {
        console.error('Error fetching GPC codes:', error);
        // Show user-friendly error notification
        // Note: Using Vue instance snackbar if available
        if (this._vm && this._vm.$snackbar) {
          this._vm.$snackbar.error('Kan GPC codes niet laden. Vernieuw de pagina.');
        }
        commit('SET_GPC_CODES', []); // Set empty array on error
      } finally {
        commit('SET_GPC_CODES_LOADING', false);
      }
    },

    /**
     * Request GPC proposals for the rows without a GPC that the import/dashboard
     * filter does not hide.
     *
     * Blocks of 100, one after another, once per row per opening. Fills only an
     * empty GPC field. Failures never reach the steward (console only); the first
     * failed call of any kind stops the remaining blocks (earlier blocks stay).
     * A reset of the proposal state (screen reopened) aborts a run in flight.
     *
     * @returns {Promise<Array<String>>} Ids of the rows that got a proposal
     */
    async fetchGpcSuggestions({ commit, state }) {
      const memory = state.gpcRequestedIds;
      const isEmpty = (p) => !String(p.productInformation?.gpcCategoryCode ?? '').trim();
      const importFilter = state.filterImportedOnly && state.importedProductIds.length > 0;
      const collect = () => {
        const rows = [];
        state.products.forEach(product => {
          if (state.gpcRequestedIds[product._id] || !isEmpty(product)) return;
          if (importFilter && !state.importedProductIds.includes(product.transactionId || product._id)) return;
          const row = buildSuggestionRequest(product);
          if (row) rows.push(row);
        });
        return rows;
      };

      if (collect().length === 0) return [];

      // The cell may be loading the list already; only fetch it when nobody is
      if (state.gpcCodesList.length === 0 && !state.gpcCodesLoading) {
        try {
          const response = await bulkEditService.getGpcCodes();
          commit('SET_GPC_CODES', response.data);
        } catch (error) {
          console.warn('GPC suggestions skipped: GPC list not available', error);
          return [];
        }
        if (state.gpcRequestedIds !== memory) return [];
      }

      // Re-collect: state may have moved while the list was loading
      const candidates = collect();
      if (candidates.length === 0) return [];
      commit('MARK_GPC_REQUESTED', candidates.map(r => r.id));
      const applied = [];

      for (const block of chunk(candidates, GPC_SUGGESTION_BLOCK_SIZE)) {
        let body;
        try {
          body = await bulkEditService.getGpcSuggestions(block);
        } catch (error) {
          if (state.gpcRequestedIds !== memory) return applied;
          const status = error?.response?.status;
          (status === 422 ? console.error : console.warn)('GPC suggestions failed:', status || error);
          break;
        }
        if (state.gpcRequestedIds !== memory) return applied;
        if (!body || body.degraded || !Array.isArray(body.results)) {
          console.warn('GPC suggestions unavailable (degraded or malformed answer)');
          break;
        }

        const sent = new Set(block.map(r => r.id));
        const knownCodes = new Set(state.gpcCodesList.map(item => item.code));
        const byId = new Map(state.products.map(p => [p._id, p]));
        for (const result of body.results) {
          const product = sent.has(result?.productId) && byId.get(result.productId);
          // Live state: the steward may have typed, focused or sorted meanwhile
          if (
            !product ||
            !/^\d{8}$/.test(String(result.gpcCode ?? '')) ||
            !knownCodes.has(result.gpcCode) ||
            !isEmpty(product) ||
            state.gpcFocusedIds[product._id] ||
            state.sortBy === 'gpcCategoryCode' ||
            state.filters.gpcCategoryCode
          ) {
            continue;
          }
          const count = Number(result.basis?.comparableProducts);
          commit('UPDATE_PRODUCT_FIELD', {
            productId: product._id,
            field: 'gpcCategoryCode',
            value: result.gpcCode
          });
          commit('SET_GPC_SUGGESTION', {
            productId: product._id,
            suggestion: {
              code: result.gpcCode,
              name: result.gpcName,
              comparableProducts: Number.isFinite(count) ? count : 0,
              gtins: Array.isArray(result.basis?.gtins)
                ? result.basis.gtins.filter(g => typeof g === 'string')
                : []
            }
          });
          applied.push(product._id);
        }
      }
      return applied;
    },

    /**
     * Generate missing functional names for imported products
     *
     * Identifies products that have a GPC code but no functional name,
     * then batch-generates functional names using AI service.
     * Processes in batches of 5 to respect API rate limits.
     *
     * @param {Object} context - Vuex action context
     * @param {Object} [payload] - Optional { productIds }: only generate for these rows
     * @returns {Promise<void>}
     */
    async generateMissingFunctionalNames({ commit, state }, payload) {
      const onlyIds = payload?.productIds ? new Set(payload.productIds) : null;

      // Import AI service
      const aiService = require('@/api/ai/aiService.js').default;

      // Check if AI service is available
      if (!aiService.isAvailable()) {
        console.warn('AI service not available. Skipping functional name generation.');
        return;
      }

      // Find products that need functional names:
      // - Have a GPC code
      // - Don't have a functional name (empty or missing)
      // - Are in the imported products list (if filtering is active)
      const productsToProcess = state.products.filter(product => {
        const pi = product.productInformation;
        if (!pi) return false;
        if (onlyIds && !onlyIds.has(product._id)) return false;

        const hasGpcCode = pi.gpcCategoryCode && pi.gpcCategoryCode.trim() !== '';
        const missingFunctionalName = !pi.functionalName || pi.functionalName.trim() === '';

        // If we have imported product IDs, only process those
        if (state.importedProductIds.length > 0) {
          const productId = product.transactionId || product._id;
          const isImported = state.importedProductIds.includes(productId);
          return hasGpcCode && missingFunctionalName && isImported;
        }

        return hasGpcCode && missingFunctionalName;
      });

      // If no products need processing, exit early
      if (productsToProcess.length === 0) {
        console.log('No products need functional name generation.');
        return;
      }

      console.log(`Generating functional names for ${productsToProcess.length} products...`);

      // Initialize progress
      commit('SET_AI_GENERATION_PROGRESS', {
        isGenerating: true,
        total: productsToProcess.length,
        completed: 0,
        current: null
      });

      // Ensure GPC codes are loaded (needed for descriptions)
      if (state.gpcCodesList.length === 0) {
        try {
          const bulkEditService = require('@/api/bulkEdit/bulkEditService.js').default;
          const response = await bulkEditService.getGpcCodes();
          commit('SET_GPC_CODES', response.data);
        } catch (error) {
          console.error('Failed to load GPC codes:', error);
          commit('RESET_AI_GENERATION_PROGRESS');
          return;
        }
      }

      // Process in batches of 5 to avoid overwhelming the API
      const BATCH_SIZE = 5;
      let completed = 0;

      for (let i = 0; i < productsToProcess.length; i += BATCH_SIZE) {
        const batch = productsToProcess.slice(i, i + BATCH_SIZE);

        // Process batch in parallel
        const promises = batch.map(async (product) => {
          const pi = product.productInformation;
          const productName = pi.productName || pi.tradeItemDescription || 'Product';

          // Update current product being processed
          commit('SET_AI_GENERATION_PROGRESS', {
            current: productName
          });

          try {
            // Find GPC description from codes list
            const gpcItem = state.gpcCodesList.find(item => item.code === pi.gpcCategoryCode);
            if (!gpcItem || !gpcItem.nameNL) {
              console.warn(`GPC description not found for code: ${pi.gpcCategoryCode}`);
              return;
            }

            // Generate functional name
            const functionalName = await aiService.generateFunctionalName(
              gpcItem.nameNL,
              pi.productName || '',
              pi.productBrandName || null
            );

            // The steward may have filled the name while the AI was working
            if (pi.functionalName && pi.functionalName.trim() !== '') return;

            // Update product in store
            commit('UPDATE_PRODUCT_FIELD', {
              productId: product._id,
              field: 'functionalName',
              value: functionalName
            });

            console.log(`Generated functional name "${functionalName}" for product: ${productName}`);

          } catch (error) {
            console.error(`Failed to generate functional name for ${productName}:`, error);
            // Continue with other products, don't fail the whole batch
          }
        });

        // Wait for batch to complete
        await Promise.all(promises);

        // Update progress
        completed += batch.length;
        commit('SET_AI_GENERATION_PROGRESS', {
          completed: completed
        });
      }

      // Generation complete
      commit('SET_AI_GENERATION_PROGRESS', {
        isGenerating: false,
        current: null
      });

      console.log(`Functional name generation complete. Processed ${completed} products.`);
    },

    /**
     * Regenerate functional names for ALL products with GPC codes
     *
     * Unlike generateMissingFunctionalNames, this action:
     * - Processes ALL products with GPC codes (not just those missing functional names)
     * - OVERWRITES existing functional names
     * - Uses filteredProducts to respect current filters
     *
     * @param {Object} context - Vuex action context
     * @returns {Promise<void>}
     */
    async regenerateAllFunctionalNames({ commit, state, getters }) {
      // Import AI service
      const aiService = require('@/api/ai/aiService.js').default;

      // Check if AI service is available
      if (!aiService.isAvailable()) {
        console.warn('AI service not available. Skipping functional name regeneration.');
        return;
      }

      // Find all products with GPC codes in filtered view (includes all pages)
      const productsToProcess = getters.filteredProducts.filter(product => {
        const pi = product.productInformation;
        if (!pi) return false;

        const hasGpcCode = pi.gpcCategoryCode && pi.gpcCategoryCode.trim() !== '';
        return hasGpcCode;
      });

      // If no products to process, exit early
      if (productsToProcess.length === 0) {
        console.log('No products with GPC codes to regenerate.');
        return;
      }

      console.log(`Regenerating functional names for ${productsToProcess.length} products...`);

      // Initialize progress
      commit('SET_AI_GENERATION_PROGRESS', {
        isGenerating: true,
        total: productsToProcess.length,
        completed: 0,
        current: null
      });

      // Ensure GPC codes are loaded (needed for descriptions)
      if (state.gpcCodesList.length === 0) {
        try {
          const bulkEditService = require('@/api/bulkEdit/bulkEditService.js').default;
          const response = await bulkEditService.getGpcCodes();
          commit('SET_GPC_CODES', response.data);
        } catch (error) {
          console.error('Failed to load GPC codes:', error);
          commit('RESET_AI_GENERATION_PROGRESS');
          return;
        }
      }

      // Process in batches of 5 to avoid overwhelming the API
      const BATCH_SIZE = 5;
      let completed = 0;

      for (let i = 0; i < productsToProcess.length; i += BATCH_SIZE) {
        const batch = productsToProcess.slice(i, i + BATCH_SIZE);

        // Process batch in parallel
        const promises = batch.map(async (product) => {
          const pi = product.productInformation;
          const productName = pi.productName || pi.tradeItemDescription || 'Product';

          // Update current product being processed
          commit('SET_AI_GENERATION_PROGRESS', {
            current: productName
          });

          try {
            // Find GPC description from codes list
            const gpcItem = state.gpcCodesList.find(item => item.code === pi.gpcCategoryCode);
            if (!gpcItem || !gpcItem.nameNL) {
              console.warn(`GPC description not found for code: ${pi.gpcCategoryCode}`);
              return;
            }

            // Generate functional name (this will OVERWRITE existing value)
            const functionalName = await aiService.generateFunctionalName(
              gpcItem.nameNL,
              pi.productName || '',
              pi.productBrandName || null
            );

            // Update product in store
            commit('UPDATE_PRODUCT_FIELD', {
              productId: product._id,
              field: 'functionalName',
              value: functionalName
            });

            console.log(`Regenerated functional name "${functionalName}" for product: ${productName}`);

          } catch (error) {
            console.error(`Failed to regenerate functional name for ${productName}:`, error);
            // Continue with other products, don't fail the whole batch
          }
        });

        // Wait for batch to complete
        await Promise.all(promises);

        // Update progress
        completed += batch.length;
        commit('SET_AI_GENERATION_PROGRESS', {
          completed: completed
        });
      }

      // Generation complete
      commit('SET_AI_GENERATION_PROGRESS', {
        isGenerating: false,
        current: null
      });

      console.log(`Functional name regeneration complete. Processed ${completed} products.`);
    }
  }
};
